import React from 'react';

import { createStyles, Group, Loader, Popover, SegmentedControl, Stack, Text } from '@mantine/core';
import { DatePicker, DateValue } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { CalendarNav, CalendarNext, CalendarPrev, CalendarToday } from '@mobiscroll/react';
import { IconCalendarEvent, IconCircleCheckFilled, IconCircleXFilled } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useStaffAvailabilityCalendarIsFetching } from 'hooks/staffAvailability/useStaffAvailabilityCalendar';

import { EventCalendarView } from './types';

export type LoadStatus = 'loading' | 'loaded' | 'error';

const getStatusText = (label: string, status: LoadStatus): string => {
  switch (status) {
    case 'loaded':
      return `${label} loaded`;
    case 'error':
      return `${label} failed to load`;
    case 'loading':
    default:
      return `${label} loading`;
  }
};

const getStatusColor = (status: LoadStatus): 'green' | 'red' | 'gray' => {
  switch (status) {
    case 'loaded':
      return 'green';
    case 'error':
      return 'red';
    case 'loading':
    default:
      return 'gray';
  }
};

const useStyles = createStyles(theme => ({
  headerWrapper: {
    width: '100%',
    fontFamily: `${theme.fontFamily} !important`,
    fontSize: theme.fontSizes.sm,
    padding: theme.spacing.sm,
    [theme.fn.smallerThan('sm')]: {
      paddingLeft: '2px',
      paddingRight: '2px',
    },
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridTemplateRows: 'auto',
    gridTemplateAreas: ` 
        "calnav segmented calprevnext"
      `,
    alignItems: 'center',
  },
  calnav: {
    gridArea: 'calnav',
    justifySelf: 'left',
    [theme.fn.smallerThan('sm')]: {
      fontSize: theme.fontSizes.xs,
    },
  },
  segmented: {
    gridArea: 'segmented',
    maxWidth: 'max-content',
    justifySelf: 'center',
  },
  calprevnext: {
    gridArea: 'calprevnext',
    maxWidth: 'max-content',
    justifySelf: 'right',
    [theme.fn.smallerThan('sm')]: {
      fontSize: theme.fontSizes.xs,
    },
  },
  fontOverride: {
    fontFamily: `${theme.fontFamily} !important`,
  },
}));

interface StatusIndicatorProps {
  label: string;
  status: LoadStatus;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ label, status }) => {
  const { theme } = useStyles();

  return (
    <Group spacing='xs' noWrap>
      <Text color={getStatusColor(status)} fs='italic'>
        {getStatusText(label, status)}
      </Text>

      {status === 'loading' && <Loader size='xs' color='green' />}

      {status === 'loaded' && (
        <IconCircleCheckFilled size={theme.fontSizes.md} style={{ color: theme.colors.green[7] }} />
      )}

      {status === 'error' && (
        <IconCircleXFilled size={theme.fontSizes.md} style={{ color: theme.colors.red[7] }} />
      )}
    </Group>
  );
};

interface IEventCalendarNavigationButtonsProps {
  view: EventCalendarView;
  setView: React.Dispatch<React.SetStateAction<EventCalendarView>>;
  isDesktop: boolean;
  eventsStatus?: LoadStatus;
  selectedDate?: Date;
  coachAvailabilityStatus?: LoadStatus;
  onDateSelect?: (date: Date) => void;
}

const EventCalendarNavigationButtons = ({
  view,
  setView,
  isDesktop,
  eventsStatus = 'loading',
  selectedDate,
  coachAvailabilityStatus: coachAvailabilityStatusProp,
  onDateSelect,
}: IEventCalendarNavigationButtonsProps): JSX.Element => {
  const { classes, theme } = useStyles();
  const [datePickerOpened, { open: openDatePicker, close: closeDatePicker }] = useDisclosure(false);

  const handleDatePickerChange = (date: DateValue): void => {
    if (date && onDateSelect) {
      onDateSelect(date);
      closeDatePicker();
    }
  };

  const isFetchingCoachAvailability = useStaffAvailabilityCalendarIsFetching();
  const coachAvailabilityStatus: LoadStatus = coachAvailabilityStatusProp ?? 'loading';
  const googleCalendarStatus: LoadStatus = coachAvailabilityStatus;

  const isDayView = view === 'day';

  const FetchingCoachAvailabilityStatus = (
    <Group spacing='sm'>
      <Text color={isFetchingCoachAvailability ? 'gray' : 'green'} fs='italic'>
        {isFetchingCoachAvailability ? 'Fetching Calendar Information' : 'All Calendar Information Loaded'}
      </Text>
      {isFetchingCoachAvailability ? (
        <Loader size='sm' color='green' />
      ) : (
        <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.green[7] }} />
      )}
    </Group>
  );

  return (
    <div className={classes.headerWrapper}>
      <div className={classes.header}>
        {view === 'day' && selectedDate ? (
          <Text className={classes.calnav} weight={700} size='xl'>
            {dayjs(selectedDate).format('dddd - MMMM D, YYYY')}
          </Text>
        ) : (
          <CalendarNav className={classes.calnav} />
        )}
        <div className={classes.segmented}>
          <SegmentedControl
            data-cy='calendarViewSwitcher'
            size={isDesktop ? 'sm' : 'xs'}
            value={view}
            onChange={(value: EventCalendarView) => setView(value)}
            data={[
              { label: 'Day', value: 'day' },
              { label: 'Week', value: 'week' },
              { label: 'Month', value: 'month' },
            ]}
          />
        </div>
        <Group position='right' spacing='xl'>
          {isDayView && isDesktop ? (
            <Stack spacing={2}>
              <StatusIndicator label='Google calendar availability' status={googleCalendarStatus} />
              <StatusIndicator label='Coach availability settings' status={coachAvailabilityStatus} />
              <StatusIndicator label='Events' status={eventsStatus} />
            </Stack>
          ) : (
            FetchingCoachAvailabilityStatus
          )}
          <Group noWrap spacing={0} className={classes.calprevnext}>
            <CalendarPrev className={classes.fontOverride} />
            {isDayView && selectedDate && onDateSelect ? (
              <Popover
                opened={datePickerOpened}
                onClose={closeDatePicker}
                closeOnEscape
                position='bottom'
                shadow='md'
                radius='md'
                withinPortal
              >
                <Popover.Target>
                  <Group onClick={openDatePicker} noWrap spacing={4} sx={{ cursor: 'pointer' }}>
                    <Text className={classes.fontOverride} size='sm' color='kiloBlue'>
                      {dayjs(selectedDate).isSame(dayjs(), 'day')
                        ? 'Today'
                        : dayjs(selectedDate).format('MMMM D')}
                    </Text>
                    <IconCalendarEvent size={16} color={theme.colors.kiloBlue[8]} />
                  </Group>
                </Popover.Target>
                <Popover.Dropdown>
                  <DatePicker value={selectedDate} onChange={handleDatePickerChange} />
                </Popover.Dropdown>
              </Popover>
            ) : (
              <CalendarToday className={classes.fontOverride} />
            )}
            <CalendarNext className={classes.fontOverride} />
          </Group>
        </Group>
      </div>
    </div>
  );
};

export default EventCalendarNavigationButtons;
